import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DateTime as DT } from 'luxon';
import type { DocumentDirectoryType } from '@econtractor/types';
import { FilesCloudUpload } from 'modules/files';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import { getProposalIsUahMode } from 'modules/templates';
import { getCurrencyI18nParams } from 'utils/helpers/currency/currencyHelpers';

const DOCUMENT_TYPE_OPTIONS: DocumentDirectoryType[] = ['invoice', 'contract', 'act', 'other'];
const PROPOSAL_NUMBER_MAX_LENGTH = 250;
const COMMENT_MAX_LENGTH = 500;

export interface UploadDocumentFormValues {
    proposalType: DocumentDirectoryType;
    proposalName: string;
    proposalNumber: string;
    proposalDt: number | null;
    generationComment: string;
    proposalAmount: string;
    markPaid: boolean;
    pricesInUsd: boolean;
    proposalVat: boolean;
}

export interface UploadDocumentFormProps {
    values: UploadDocumentFormValues;
    onChange: Dispatch<SetStateAction<UploadDocumentFormValues>>;
    rate: number;
    loading?: boolean;
    uploadProgress: number | boolean;
    files: File[];
    setFiles: Dispatch<SetStateAction<File[]>>;
    onSubmit: (values: UploadDocumentFormValues) => Promise<void>;
    allowMarkPaid?: boolean;
    fileLocked?: boolean;
}

const millisToIsoDate = (millis: number | null | undefined): string => {
    const value = Number(millis || 0);
    if (!Number.isFinite(value) || value <= 0) return '';
    return DT.fromMillis(value).toISODate() || '';
};

const isoDateToMillis = (isoDate: string): number | null => {
    const raw = String(isoDate || '').trim();
    if (!raw) return null;
    const parsed = DT.fromISO(raw).startOf('day');
    return parsed.isValid ? parsed.toMillis() : null;
};

const UploadDocumentForm = ({
    values,
    onChange,
    rate,
    loading = false,
    uploadProgress,
    files,
    setFiles,
    onSubmit,
    allowMarkPaid = false,
    fileLocked = false,
}: UploadDocumentFormProps) => {
    const { t } = useTranslation(['Document', 'Objects', 'common']);
    const refName = useRef<HTMLInputElement>(null);
    const isUahMode = getProposalIsUahMode(rate);
    const busy = loading || uploadProgress !== false;

    useEffect(() => {
        setTimeout(() => {
            refName.current?.focus();
        }, 100);
    }, []);

    useEffect(() => {
        if (!files.length || fileLocked) return;
        onChange((prev) => {
            if (String(prev.proposalName || '').trim()) return prev;
            const baseName = files[0].name.replace(/\.[^.]+$/, '').trim();
            return baseName ? { ...prev, proposalName: baseName } : prev;
        });
    }, [files, fileLocked, onChange]);

    const handleFilesChange: Dispatch<SetStateAction<File[]>> = (nextFiles) => {
        if (typeof nextFiles === 'function') {
            setFiles((prev) => nextFiles(prev).slice(-1));
            return;
        }
        setFiles(nextFiles.slice(-1));
    };

    const handleChange = <K extends keyof UploadDocumentFormValues>(
        field: K,
        value: UploadDocumentFormValues[K],
    ) => {
        onChange((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <Form
            className="d-flex flex-column h-100"
            onSubmit={async (e) => {
                e.preventDefault();
                await onSubmit(values);
            }}
        >
            <div className="off-client-edit-form d-flex flex-column h-100">
                <div className="off-client-edit-content px-3">
                    <Row className="g-2 mb-3">
                        <Col xs={12} sm={8} className="min-w-0">
                            <Form.Group>
                                <Form.Label>{t('Document:edit.fields.proposalName')}</Form.Label>
                                <Form.Control
                                    required
                                    ref={refName}
                                    disabled={busy}
                                    value={values.proposalName}
                                    onChange={(e) => handleChange('proposalName', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} sm={4} className="min-w-0">
                            <Form.Group>
                                <Form.Label>{t('Document:edit.fields.proposalType')}</Form.Label>
                                <Form.Select
                                    required
                                    disabled={busy}
                                    value={values.proposalType}
                                    onChange={(e) =>
                                        handleChange('proposalType', e.target.value as DocumentDirectoryType)
                                    }
                                >
                                    {DOCUMENT_TYPE_OPTIONS.map((type) => (
                                        <option key={type} value={type}>
                                            {t(`Document:documentTypes.${type}`)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="g-2 mb-3">
                        <Col xs={12} sm={7} className="min-w-0">
                            <Form.Group>
                                <Form.Label>{t('Document:edit.fields.proposalNumber')}</Form.Label>
                                <Form.Control
                                    disabled={busy}
                                    value={values.proposalNumber}
                                    maxLength={PROPOSAL_NUMBER_MAX_LENGTH}
                                    onChange={(e) =>
                                        handleChange(
                                            'proposalNumber',
                                            e.target.value.slice(0, PROPOSAL_NUMBER_MAX_LENGTH),
                                        )
                                    }
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} sm={5} className="min-w-0">
                            <Form.Group>
                                <Form.Label>{t('Document:edit.fields.proposalDt')}</Form.Label>
                                <Form.Control
                                    type="date"
                                    required
                                    disabled={busy}
                                    value={millisToIsoDate(values.proposalDt)}
                                    onChange={(e) =>
                                        handleChange('proposalDt', isoDateToMillis(e.target.value))
                                    }
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('Document:edit.fields.proposalComment')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            disabled={busy}
                            value={values.generationComment}
                            maxLength={COMMENT_MAX_LENGTH}
                            onChange={(e) =>
                                handleChange(
                                    'generationComment',
                                    e.target.value.slice(0, COMMENT_MAX_LENGTH),
                                )
                            }
                        />
                        <div className="small text-muted text-end mt-1">
                            {String(values.generationComment || '').length} / {COMMENT_MAX_LENGTH}
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>{t('Document:edit.generate.logAmount')}</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            disabled={busy}
                            value={values.proposalAmount}
                            onChange={(e) => handleChange('proposalAmount', e.target.value)}
                        />
                    </Form.Group>

                    {allowMarkPaid ? (
                        <div className="form-check form-switch mb-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="upload-document-paid"
                                checked={Boolean(values.markPaid)}
                                disabled={busy}
                                onChange={(e) => handleChange('markPaid', Boolean(e.target.checked))}
                            />
                            <label className="form-check-label" htmlFor="upload-document-paid">
                                {t('Objects:documents.upload.markPaid')}
                            </label>
                            <div className="small text-muted">
                                {t('Objects:documents.upload.markPaidHint')}
                            </div>
                        </div>
                    ) : null}

                    {!isUahMode ? (
                        <div className="form-check form-switch mb-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="upload-document-usd"
                                checked={Boolean(values.pricesInUsd)}
                                disabled={busy}
                                onChange={(e) => handleChange('pricesInUsd', Boolean(e.target.checked))}
                            />
                            <label className="form-check-label" htmlFor="upload-document-usd">
                                {t('Document:edit.general.usdLabel', getCurrencyI18nParams())}
                            </label>
                            <div className="small text-muted">
                                {values.pricesInUsd
                                    ? t('Document:edit.general.usdHintUsd', getCurrencyI18nParams())
                                    : t('Document:edit.general.usdHintUah', {
                                          ...getCurrencyI18nParams(),
                                          rate: Number(rate || 0).toFixed(2),
                                      })}
                            </div>
                        </div>
                    ) : null}

                    <FilesCloudUpload
                        disabled={busy}
                        progress={uploadProgress}
                        files={files}
                        setFiles={handleFilesChange}
                        label={
                            fileLocked
                                ? t('Objects:documents.uploadAi.fileFromAi')
                                : t('Objects:documents.upload.fileLabel')
                        }
                        buttonLabel={t('common:buttons.upload')}
                        uploadingLabel={t('common:buttons.uploading')}
                    />
                </div>

                <div className="p-3 offcanvas-footer off-client-edit-footer">
                    <div className="d-flex justify-content-end">
                        <Button type="submit" variant="info" disabled={busy} className="min100px">
                            {spinnerTitle(
                                <>
                                    <SvgSave className="me-2 mtop-2" />
                                    {t('Objects:documents.upload.btnSubmit')}
                                </>,
                                busy,
                                t('common:buttons.uploading'),
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default UploadDocumentForm;
